/**
 * Homepage motion: GSAP ScrollTrigger reveals on the page's scroll container. Skipped entirely under reduced motion, so every
 * element keeps its final, static state.
 *
 * Markup hooks: [data-hero-image] eases in and drifts slightly on scroll;
 * [data-reveal="words"] staggers its `.reveal-word`s; [data-reveal="items"]
 * fades its children up. Anything already on screen at load is left alone so
 * server-rendered content never flashes away.
 */
import { useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import type { RefObject } from "react";

export function useHomepageMotion(rootRef: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const root = rootRef.current;
    const scroller = document.querySelector("main");
    if (!root || !scroller) return;
    if (globalThis.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }

    gsap.registerPlugin(ScrollTrigger);

    const belowFold = (el: Element) =>
      el.getBoundingClientRect().top > globalThis.innerHeight;

    const ctx = gsap.context(() => {
      const heroImage = root.querySelector("[data-hero-image]");
      if (heroImage) {
        gsap.fromTo(
          heroImage,
          { scale: 1.06 },
          { scale: 1, duration: 1.6, ease: "power2.out" },
        );
        gsap.to(heroImage, {
          yPercent: 4,
          ease: "none",
          scrollTrigger: {
            trigger: heroImage,
            scroller,
            start: "top top",
            end: "bottom top",
            scrub: true,
          },
        });
      }

      for (const el of root.querySelectorAll('[data-reveal="words"]')) {
        if (!belowFold(el)) continue;
        gsap.from(el.querySelectorAll(".reveal-word"), {
          yPercent: 110,
          duration: 0.7,
          ease: "power3.out",
          stagger: 0.05,
          scrollTrigger: {
            trigger: el,
            scroller,
            start: "top 88%",
            once: true,
          },
        });
      }

      for (const el of root.querySelectorAll('[data-reveal="items"]')) {
        if (!belowFold(el)) continue;
        gsap.from(el.children, {
          y: 24,
          opacity: 0,
          duration: 0.6,
          ease: "power2.out",
          stagger: 0.06,
          scrollTrigger: {
            trigger: el,
            scroller,
            start: "top 90%",
            once: true,
          },
        });
      }
    }, root);

    // Images and fonts change the page height after the first measure.
    const refresh = () => ScrollTrigger.refresh();
    globalThis.addEventListener("load", refresh);
    void document.fonts.ready.then(refresh);

    return () => {
      globalThis.removeEventListener("load", refresh);
      ctx.revert();
    };
  }, [rootRef]);
}
